import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';

const AdminDocumentation = () => {
  const { t } = useTranslation();
  const [activeSection, setActiveSection] = useState('overview');

  const sections = [
    { id: 'overview', title: t('admin.adminDocs.overview'), icon: '📋' },
    { id: 'seasons', title: t('admin.adminDocs.seasons'), icon: '🏆' },
    { id: 'fantasy', title: t('admin.adminDocs.fantasy'), icon: '⭐' },
    { id: 'fantasyDraft', title: t('admin.adminDocs.fantasyDraft'), icon: '🎲' },
    { id: 'players', title: t('admin.adminDocs.players'), icon: '👥' },
    { id: 'games', title: t('admin.adminDocs.games'), icon: '🏀' },
    { id: 'ocr', title: t('admin.adminDocs.ocr'), icon: '📸' },
    { id: 'playoffs', title: t('admin.adminDocs.playoffs'), icon: '🏅' },
    { id: 'trades', title: t('admin.adminDocs.trades'), icon: '🔄' },
    { id: 'invites', title: t('admin.adminDocs.invites'), icon: '🎫' },
    { id: 'users', title: t('admin.adminDocs.users'), icon: '👤' },
    { id: 'backup', title: t('admin.adminDocs.backup'), icon: '💾' },
    { id: 'troubleshooting', title: t('admin.adminDocs.troubleshooting'), icon: '🔧' }
  ];

  const csvTemplate = `First Name,Last Name,Position,Overall Rating,Team Name,Badges
Shaquille,ONeal,C,96,Lakers,18
Kobe,Bryant,SG,94,Lakers,15
Magic,Johnson,PG,95,Lakers,16`;

  const renderContent = () => {
    switch (activeSection) {
      case 'overview':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.adminDocs.overviewTitle')}</h2>
            
            <div className="grid md:grid-cols-2 gap-6 mb-8">
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="font-bold text-lg mb-3 text-blue-600 dark:text-blue-400">🎯 {t('admin.adminDocs.mainFunctions')}</h3>
                <ul className="space-y-2 text-sm">
                  <li>• {t('admin.adminDocs.mainFunctionsList.createSeasons')}</li>
                  <li>• {t('admin.adminDocs.mainFunctionsList.scheduleGames')}</li>
                  <li>• {t('admin.adminDocs.mainFunctionsList.managePlayoffs')}</li>
                  <li>• {t('admin.adminDocs.mainFunctionsList.handleTrades')}</li>
                  <li>• {t('admin.adminDocs.mainFunctionsList.inviteOwners')}</li>
                  <li>• {t('admin.adminDocs.mainFunctionsList.confirmApplications')}</li>
                </ul>
              </div>
              
              <div className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="font-bold text-lg mb-3 text-green-600 dark:text-green-400">🔄 {t('admin.adminDocs.typicalFlow')}</h3>
                <ol className="space-y-2 text-sm">
                  <li>1. {t('admin.adminDocs.typicalFlowSteps.step1')}</li>
                  <li>2. {t('admin.adminDocs.typicalFlowSteps.step2')}</li>
                  <li>3. {t('admin.adminDocs.typicalFlowSteps.step3')}</li>
                  <li>4. {t('admin.adminDocs.typicalFlowSteps.step4')}</li>
                  <li>5. {t('admin.adminDocs.typicalFlowSteps.step5')}</li>
                  <li>6. {t('admin.adminDocs.typicalFlowSteps.step6')}</li>
                </ol>
              </div>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-yellow-800 mb-2">⚠️ {t('admin.adminDocs.importantNotes')}</h3>
              <ul className="text-sm text-yellow-700 dark:text-yellow-400 space-y-1">
                <li>• {t('admin.adminDocs.importantNotesList.activateSeason')}</li>
                <li>• {t('admin.adminDocs.importantNotesList.onlyOneActive')}</li>
                <li>• {t('admin.adminDocs.importantNotesList.fantasyRequiresImport')}</li>
                <li>• {t('admin.adminDocs.importantNotesList.backupRegularly')}</li>
              </ul>
            </div>
          </div>
        );

      case 'seasons':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.adminDocs.seasonManagementTitle')}</h2>
            
            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">{t('admin.adminDocs.createStandardSeason')}</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>{t('admin.adminDocs.seasonSteps.navigate')}</strong><br/>
                      {t('admin.adminDocs.seasonSteps.navigateDesc')}
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>{t('admin.adminDocs.seasonSteps.createNew')}</strong><br/>
                      {t('admin.adminDocs.seasonSteps.createNewDesc')}
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>{t('admin.adminDocs.seasonSteps.selectType')}</strong><br/>
                      {t('admin.adminDocs.seasonSteps.selectTypeDesc')}
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>{t('admin.adminDocs.seasonSteps.configurePlayoffs')}</strong><br/>
                      {t('admin.adminDocs.seasonSteps.configurePlayoffsDesc')}
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                    <div>
                      <strong>{t('admin.adminDocs.seasonSteps.activateSeason')}</strong><br/>
                      {t('admin.adminDocs.seasonSteps.activateSeasonDesc')}
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">{t('admin.adminDocs.seasonActions')}</h3>
                <div className="grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-semibold mb-2">✅ {t('admin.adminDocs.seasonActionsList.activate')}</h4>
                    <p>{t('admin.adminDocs.seasonActionsList.activateDesc')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">🏁 {t('admin.adminDocs.seasonActionsList.complete')}</h4>
                    <p>{t('admin.adminDocs.seasonActionsList.completeDesc')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">✏️ {t('admin.adminDocs.seasonActionsList.edit')}</h4>
                    <p>{t('admin.adminDocs.seasonActionsList.editDesc')}</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">🗑️ {t('admin.adminDocs.seasonActionsList.delete')}</h4>
                    <p>{t('admin.adminDocs.seasonActionsList.deleteDesc')}</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'fantasy':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.adminDocs.fantasySeasonTitle')}</h2>
            
            <div className="bg-purple-50 border border-purple-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-purple-800 mb-2">💡 {t('admin.adminDocs.fantasyWhat')}</h3>
              <p className="text-purple-700 text-sm">
                {t('admin.adminDocs.fantasyWhatDesc')}
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">{t('admin.adminDocs.createFantasyTitle')}</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Create Season with Custom Type</strong><br/>
                      In Season Management, select "Custom" as Season Type
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Specify Roster Era</strong><br/>
                      Enter descriptive era (e.g., "2000s Lakers Era", "90s Bulls Dynasty")
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Copy CSV Template</strong><br/>
                      Copy the displayed CSV template format for your custom roster
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Create Your CSV File</strong><br/>
                      Fill in your custom players following the template format
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                    <div>
                      <strong>Import Roster</strong><br/>
                      Use "Import Custom Roster" button to upload your CSV file
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4">📋 CSV Template Format</h3>
                <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium text-gray-700 dark:text-gray-300">CSV Format:</span>
                    <button
                      onClick={() => navigator.clipboard.writeText(csvTemplate)}
                      className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700 transition"
                    >
                      Copy
                    </button>
                  </div>
                  <pre className="text-xs bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded p-3 overflow-x-auto font-mono">
                    {csvTemplate}
                  </pre>
                </div>
                
                <div className="mt-4 grid md:grid-cols-2 gap-4 text-sm">
                  <div>
                    <h4 className="font-semibold mb-2">Required Fields:</h4>
                    <ul className="space-y-1">
                      <li>• <strong>First Name:</strong> Player's first name</li>
                      <li>• <strong>Last Name:</strong> Player's last name</li>
                      <li>• <strong>Position:</strong> PG, SG, SF, PF, C (or combinations)</li>
                      <li>• <strong>Overall Rating:</strong> Number 1-99</li>
                      <li>• <strong>Team Name:</strong> Must match existing team</li>
                      <li>• <strong>Badges:</strong> Number of badges (0-20)</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Tips:</h4>
                    <ul className="space-y-1">
                      <li>• Save file with .csv extension</li>
                      <li>• Keep team names consistent</li>
                      <li>• Ratings typically range 75-99</li>
                      <li>• Badge counts: 0-6 (role players), 7-15 (stars), 16+ (superstars)</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'fantasyDraft':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.adminDocs.fantasyDraftTitle')}</h2>
            
            <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-purple-800 mb-2">🎲 What is Fantasy Draft?</h3>
              <p className="text-purple-700 text-sm">
                {t('admin.adminDocs.fantasyDraftDesc')}
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Setting Up a Fantasy Draft</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Create a Fantasy Season First</strong><br/>
                      Go to Season Management → Create Custom Season → Import your player pool via CSV
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Navigate to Fantasy Draft</strong><br/>
                      Admin Panel → Fantasy Draft Management
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Create New Fantasy Draft</strong><br/>
                      Select your fantasy season, set rounds (typically 13-15 for full rosters)
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Assign Participating Teams & GMs</strong><br/>
                      Select which teams participate and ensure all have assigned GMs
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                    <div>
                      <strong>Set Draft Order</strong><br/>
                      Choose random order or set manual order for first round
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">6</span>
                    <div>
                      <strong>Start the Draft</strong><br/>
                      Begin live draft - GMs take turns picking players in snake order
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Live Draft Management</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600 dark:text-green-400">Draft Features:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Snake Order:</strong> Draft order reverses each round</li>
                      <li>• <strong>Live Updates:</strong> Real-time draft board for all participants</li>
                      <li>• <strong>Time Limits:</strong> Set pick timers (auto-pick available)</li>
                      <li>• <strong>Player Filtering:</strong> Filter by position, rating, team</li>
                      <li>• <strong>Draft History:</strong> Track all picks in real-time</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-orange-600">Admin Controls:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Force Pick:</strong> Make pick for absent GM</li>
                      <li>• <strong>Undo Pick:</strong> Reverse mistake picks</li>
                      <li>• <strong>Pause/Resume:</strong> Control draft timing</li>
                      <li>• <strong>Skip Pick:</strong> Move to next GM if needed</li>
                      <li>• <strong>Complete Draft:</strong> Finalize and assign rosters</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'ocr':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.adminDocs.ocrTitle')}</h2>
            
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-green-800 mb-2">📸 OCR Overview</h3>
              <p className="text-green-700 dark:text-green-400 text-sm">
                {t('admin.adminDocs.ocrDesc')}
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Taking Screenshots</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600 dark:text-green-400">✅ Best Practices:</h4>
                    <ul className="space-y-2">
                      <li>• Use high resolution (1080p+)</li>
                      <li>• Ensure good lighting/contrast</li>
                      <li>• Keep screen straight (no angles)</li>
                      <li>• Zoom in on stats table</li>
                      <li>• Crop out extra UI elements</li>
                      <li>• Take separate shots for each team</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-red-600 dark:text-red-400">❌ Avoid:</h4>
                    <ul className="space-y-2">
                      <li>• Blurry or low-resolution images</li>
                      <li>• Screenshots with glare or shadows</li>
                      <li>• Rotated or distorted images</li>
                      <li>• Too much extra UI included</li>
                      <li>• Different formats between games</li>
                      <li>• Missing player names or stats</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Using OCR for Game Stats</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Navigate to Game Entry</strong><br/>
                      Games Management → Find your game → Click "Enter Stats"
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Select OCR Tab</strong><br/>
                      Click "📸 Upload Screenshots" tab in the stats entry modal
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Upload Two Screenshots</strong><br/>
                      Upload home team screenshot and away team screenshot
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Extract Stats</strong><br/>
                      Click "Extract Stats" - processing takes 10-30 seconds
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                    <div>
                      <strong>Review & Correct</strong><br/>
                      Check extracted data, fix any errors, resolve player name conflicts
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">6</span>
                    <div>
                      <strong>Submit</strong><br/>
                      Click "Submit Score & Stats" to save all data
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">Troubleshooting OCR</h3>
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">🔍 Player Name Conflicts</h4>
                    <p className="mb-2">If multiple players have similar names (e.g., "A. Smith"), you'll see a dropdown:</p>
                    <ul className="space-y-1 ml-4">
                      <li>• Review the extracted stats to determine correct player</li>
                      <li>• Select the right player from the dropdown</li>
                      <li>• System will remember your choice for future games</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">⚠️ Stat Validation Warnings</h4>
                    <p className="mb-2">System automatically validates extracted stats:</p>
                    <ul className="space-y-1 ml-4">
                      <li>• <strong>Minutes > 48:</strong> Usually invalid, check screenshot</li>
                      <li>• <strong>Made > Attempted:</strong> Fix shooting stats</li>
                      <li>• <strong>Points Mismatch:</strong> Verify point calculation</li>
                      <li>• <strong>Invalid Ranges:</strong> Check stat reasonableness</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold text-blue-600 dark:text-blue-400">📊 Accuracy Tips</h4>
                    <ul className="space-y-1 ml-4">
                      <li>• Always review extracted data before submitting</li>
                      <li>• OCR works best with consistent screenshot quality</li>
                      <li>• Manual correction is normal for complex screenshots</li>
                      <li>• Save time by using OCR for bulk data, correct details manually</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'backup':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">{t('admin.adminDocs.backupTitle')}</h2>
            
            <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-red-800 mb-2">💾 Backup Importance</h3>
              <p className="text-red-700 dark:text-red-400 text-sm">
                {t('admin.adminDocs.backupDesc')}
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Creating Backups</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Navigate to Backup Directory</strong><br/>
                      In your project root, go to the "scripts" folder
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Run Backup Script</strong><br/>
                      <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">node scripts/backup-database.js</code>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Backup Location</strong><br/>
                      Backups are saved to "backups/" folder with timestamp names
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Quick Backup Option</strong><br/>
                      Use <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">node scripts/quick-backup.js</code> for faster backups
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Backup Schedule</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">📅 Regular Schedule:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Weekly:</strong> Every Sunday before games</li>
                      <li>• <strong>Before Operations:</strong> Season creation, CSV imports</li>
                      <li>• <strong>Playoff Time:</strong> Before each playoff round</li>
                      <li>• <strong>Trade Deadline:</strong> Before processing bulk trades</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">⚡ Quick Backups:</h4>
                    <ul className="space-y-2">
                      <li>• Before admin panel operations</li>
                      <li>• After major data entry sessions</li>
                      <li>• When testing new features</li>
                      <li>• Before user role changes</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">Restoring from Backup</h3>
                <div className="bg-orange-50 p-4 rounded mb-4">
                  <p className="text-orange-700 dark:text-orange-400 text-sm font-semibold">⚠️ Warning: Restoration overwrites current data. Use with extreme caution!</p>
                </div>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Stop All Operations</strong><br/>
                      Ensure no users are active in the system
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Choose Backup File</strong><br/>
                      Identify the backup folder with timestamp you want to restore
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Run Restoration Script</strong><br/>
                      <code className="bg-gray-100 dark:bg-gray-700 px-2 py-1 rounded">node scripts/seed-from-backup.js [BACKUP_FOLDER_NAME]</code>
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Verify Restoration</strong><br/>
                      Check that all data has been restored correctly
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-orange-100 dark:bg-orange-900/30 text-orange-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">5</span>
                    <div>
                      <strong>Communicate Changes</strong><br/>
                      Inform all users about the restoration and any lost data
                    </div>
                  </li>
                </ol>
              </section>
            </div>
          </div>
        );

      case 'players':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Player Management</h2>
            
            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Adding New Players</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Navigate to Player Management</strong><br/>
                      Admin Panel → Player Management
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Click "Add Player"</strong><br/>
                      Fill in player details: name, position, rating, team
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Set Player Attributes</strong><br/>
                      Overall rating (1-99), badge count, position(s)
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Player Actions</h3>
                <div className="grid md:grid-cols-3 gap-4 text-sm">
                  <div>
                    <h4 className="font-semibold mb-2">✏️ Edit Player</h4>
                    <p>Modify player stats, position, team assignment, or rating.</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">🔄 Transfer Player</h4>
                    <p>Move player between teams (different from trades).</p>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">🗑️ Remove Player</h4>
                    <p>Delete player from current season roster.</p>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600 dark:text-orange-400">
                  📋 {t('admin.gmDocs.rosterTitle')}
                </h3>
                <p className="text-sm text-gray-700 dark:text-gray-300 mb-4">
                  {t('admin.gmDocs.rosterIntro')}
                </p>
                <ul className="space-y-2 text-sm mb-4">
                  {['hidden', 'leaders', 'entry', 'kept', 'visible'].map(effect => (
                    <li key={effect}>• {t(`admin.gmDocs.rosterEffectList.${effect}`)}</li>
                  ))}
                </ul>
                <div className="bg-orange-50 dark:bg-orange-900/20 border border-orange-200 dark:border-orange-700 rounded p-4">
                  <p className="text-sm text-orange-700 dark:text-orange-400">
                    {t('admin.adminDocs.rosterAdminNote')}
                  </p>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">Season Isolation</h3>
                <div className="bg-orange-50 border border-orange-200 rounded p-4">
                  <p className="text-sm text-orange-700 dark:text-orange-400">
                    <strong>Important:</strong> Fantasy season players are isolated per season. 
                    Players in custom seasons don't affect standard season rosters and vice versa.
                  </p>
                  <ul className="mt-2 text-sm text-orange-600 space-y-1">
                    <li>• Standard seasons share the same player pool</li>
                    <li>• Each fantasy season has its own isolated player roster</li>
                    <li>• Trades only affect players within the same season</li>
                  </ul>
                </div>
              </section>
            </div>
          </div>
        );

      case 'games':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Game Scheduling & Results</h2>
            
            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Scheduling Games</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Ensure Active Season</strong><br/>
                      Make sure you have an active season before scheduling
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Go to Games Management</strong><br/>
                      Admin Panel → Games Management
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Click "Schedule Game"</strong><br/>
                      Select teams, set date/time, assign week number
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Bulk Scheduling</strong><br/>
                      Use "Generate Weekly Schedule" for automatic matchups
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Entering Game Results</h3>
                <div className="grid md:grid-cols-2 gap-6">
                  <div>
                    <h4 className="font-semibold mb-3">Manual Entry:</h4>
                    <ol className="space-y-2 text-sm">
                      <li>1. Find scheduled game</li>
                      <li>2. Click "Enter Result"</li>
                      <li>3. Input final scores</li>
                      <li>4. Add game recap (optional)</li>
                      <li>5. Submit to update standings</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">OCR Screenshot Method:</h4>
                    <ol className="space-y-2 text-sm">
                      <li>1. Take screenshot of final score</li>
                      <li>2. Click "Upload Screenshot"</li>
                      <li>3. System extracts scores automatically</li>
                      <li>4. Verify and confirm results</li>
                      <li>5. Submit to update standings</li>
                    </ol>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Game Statuses</h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 px-2 py-1 rounded text-xs mb-2">Scheduled</div>
                    <p>Game is planned but not played yet</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 px-2 py-1 rounded text-xs mb-2">In Progress</div>
                    <p>Game is currently being played</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-green-100 dark:bg-green-900/30 text-green-800 px-2 py-1 rounded text-xs mb-2">Completed</div>
                    <p>Final score entered, standings updated</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-100 dark:bg-red-900/30 text-red-800 px-2 py-1 rounded text-xs mb-2">Cancelled</div>
                    <p>Game was cancelled or postponed</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'playoffs':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Playoffs Management</h2>
            
            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Starting Playoffs</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Complete Regular Season</strong><br/>
                      Ensure all regular season games are finished and results entered
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Go to Playoff Management</strong><br/>
                      Admin Panel → Playoff Management
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Generate Playoff Bracket</strong><br/>
                      System automatically seeds teams based on standings
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-purple-100 dark:bg-purple-900/30 text-purple-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Schedule Playoff Games</strong><br/>
                      Set dates for each playoff round
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-red-600 dark:text-red-400">Playoff Formats</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">Available Formats:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Single Elimination:</strong> One game, winner advances</li>
                      <li>• <strong>Best of 3:</strong> First to win 2 games</li>
                      <li>• <strong>Best of 5:</strong> First to win 3 games</li>
                      <li>• <strong>Best of 7:</strong> First to win 4 games</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Series Management:</h4>
                    <ul className="space-y-2">
                      <li>• Enter results for each game in series</li>
                      <li>• System tracks series progress automatically</li>
                      <li>• Winning team advances to next round</li>
                      <li>• Finals winner becomes season champion</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Managing Playoff Series</h3>
                <div className="space-y-4">
                  <div>
                    <h4 className="font-semibold mb-2">Entering Series Results:</h4>
                    <ol className="text-sm space-y-1">
                      <li>1. Navigate to active playoff series</li>
                      <li>2. Click on individual game to enter result</li>
                      <li>3. System automatically updates series status</li>
                      <li>4. When series is complete, winning team advances</li>
                    </ol>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-2">Completing Playoffs:</h4>
                    <ol className="text-sm space-y-1">
                      <li>1. Enter all games through to Finals</li>
                      <li>2. Finals winner is automatically crowned champion</li>
                      <li>3. Champion is recorded in season history</li>
                      <li>4. Consider completing the season</li>
                    </ol>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'trades':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Trade Management</h2>
            
            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Trade Approval Process</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Team Owners Propose Trade</strong><br/>
                      Owners use the trade system to propose player swaps
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Review Pending Trades</strong><br/>
                      Admin Panel → Trade Management → View pending trades
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Evaluate Trade Fairness</strong><br/>
                      Check player ratings, team needs, league balance
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Approve or Reject</strong><br/>
                      Use approve/reject buttons with optional admin notes
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Trade Evaluation Criteria</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3 text-green-600 dark:text-green-400">✅ Approve When:</h4>
                    <ul className="space-y-1">
                      <li>• Fair exchange of similar-rated players</li>
                      <li>• Both teams benefit positionally</li>
                      <li>• No obvious roster dumping</li>
                      <li>• Within reasonable trade deadline</li>
                      <li>• No collusion suspected</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-red-600 dark:text-red-400">❌ Reject When:</h4>
                    <ul className="space-y-1">
                      <li>• Severely unbalanced trade</li>
                      <li>• Star player for multiple low-rated players</li>
                      <li>• Obvious tanking attempt</li>
                      <li>• Violates league rules</li>
                      <li>• Evidence of collusion</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">Trade Statuses</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm">
                  <div className="text-center">
                    <div className="bg-yellow-100 dark:bg-yellow-900/30 text-yellow-800 px-2 py-1 rounded text-xs mb-2">Pending</div>
                    <p>Awaiting admin review</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-green-100 dark:bg-green-900/30 text-green-800 px-2 py-1 rounded text-xs mb-2">Approved</div>
                    <p>Trade processed, players swapped</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-red-100 dark:bg-red-900/30 text-red-800 px-2 py-1 rounded text-xs mb-2">Rejected</div>
                    <p>Trade denied by admin</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded text-xs mb-2">Expired</div>
                    <p>Trade offer timed out</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'invites':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Invite System</h2>
            
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-700 rounded-lg p-4 mb-6">
              <h3 className="font-semibold text-blue-800 mb-2">🎫 Invite System Overview</h3>
              <p className="text-blue-700 dark:text-blue-400 text-sm">
                The invite system allows admins to generate special codes for inviting new team owners. 
                This ensures only approved people can join as GMs and helps manage league size.
              </p>
            </div>

            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">Creating Invite Codes</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Go to Invite Code Management</strong><br/>
                      Admin Panel → Invite Codes
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Click "Generate New Code"</strong><br/>
                      System creates unique invite code automatically
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Set Code Parameters</strong><br/>
                      Optional: Set expiration date, usage limit, specific team
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Share Code with Invitee</strong><br/>
                      Send the code to the person you want to invite
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Invite Code Management</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">Code Actions:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>View Active Codes:</strong> See all unused codes</li>
                      <li>• <strong>Deactivate Code:</strong> Prevent code usage</li>
                      <li>• <strong>Check Usage:</strong> See who used which codes</li>
                      <li>• <strong>Set Expiration:</strong> Auto-expire old codes</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Best Practices:</h4>
                    <ul className="space-y-2">
                      <li>• Generate codes for specific people</li>
                      <li>• Set reasonable expiration dates</li>
                      <li>• Monitor code usage regularly</li>
                      <li>• Deactivate unused codes periodically</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Code Statuses</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                  <div className="text-center">
                    <div className="bg-green-100 dark:bg-green-900/30 text-green-800 px-2 py-1 rounded text-xs mb-2">Active</div>
                    <p>Code is valid and can be used for registration</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-blue-100 dark:bg-blue-900/30 text-blue-800 px-2 py-1 rounded text-xs mb-2">Used</div>
                    <p>Code was successfully used by someone</p>
                  </div>
                  <div className="text-center">
                    <div className="bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 px-2 py-1 rounded text-xs mb-2">Expired</div>
                    <p>Code passed expiration date or was deactivated</p>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'users':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">User Management</h2>
            
            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Confirming New GMs</h3>
                <ol className="space-y-3 text-sm">
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">1</span>
                    <div>
                      <strong>Review New Applications</strong><br/>
                      Admin Panel → User Management → Check pending applications
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">2</span>
                    <div>
                      <strong>Verify User Information</strong><br/>
                      Check username, application details, invite code usage
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">3</span>
                    <div>
                      <strong>Assign Team (if applicable)</strong><br/>
                      If specific team was intended, assign during approval
                    </div>
                  </li>
                  <li className="flex">
                    <span className="bg-green-100 dark:bg-green-900/30 text-green-800 rounded-full w-6 h-6 flex items-center justify-center text-xs font-bold mr-3 mt-0.5">4</span>
                    <div>
                      <strong>Approve or Reject</strong><br/>
                      Confirm application to grant GM access
                    </div>
                  </li>
                </ol>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-blue-600 dark:text-blue-400">User Roles & Permissions</h3>
                <div className="grid md:grid-cols-3 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3 text-red-600 dark:text-red-400">👑 Admin</h4>
                    <ul className="space-y-1">
                      <li>• Full system access</li>
                      <li>• Create/manage seasons</li>
                      <li>• Approve trades</li>
                      <li>• Manage all users</li>
                      <li>• System configuration</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-blue-600 dark:text-blue-400">🏀 Team Owner</h4>
                    <ul className="space-y-1">
                      <li>• Manage own team</li>
                      <li>• Propose trades</li>
                      <li>• View league info</li>
                      <li>• Submit game results</li>
                      <li>• Access team features</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3 text-gray-600 dark:text-gray-400">👤 User</h4>
                    <ul className="space-y-1">
                      <li>• View public pages</li>
                      <li>• See standings</li>
                      <li>• Browse player stats</li>
                      <li>• Read news</li>
                      <li>• Apply for team ownership</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-orange-600">User Actions</h3>
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">Administrative Actions:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Promote User:</strong> Change role (user → owner → admin)</li>
                      <li>• <strong>Demote User:</strong> Reduce permissions</li>
                      <li>• <strong>Assign Team:</strong> Give ownership of specific team</li>
                      <li>• <strong>Remove Team:</strong> Take away team ownership</li>
                      <li>• <strong>Deactivate User:</strong> Temporarily disable account</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">Monitoring:</h4>
                    <ul className="space-y-2">
                      <li>• <strong>Activity Logs:</strong> Track user actions</li>
                      <li>• <strong>Login History:</strong> Monitor account usage</li>
                      <li>• <strong>Team Performance:</strong> Review GM effectiveness</li>
                      <li>• <strong>Trade History:</strong> Check trading patterns</li>
                      <li>• <strong>Violation Tracking:</strong> Note rule violations</li>
                    </ul>
                  </div>
                </div>
              </section>
            </div>
          </div>
        );

      case 'troubleshooting':
        return (
          <div>
            <h2 className="text-2xl font-bold mb-4">Troubleshooting</h2>
            
            <div className="space-y-6">
              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-red-600 dark:text-red-400">Common Issues</h3>
                
                <div className="space-y-6">
                  <div>
                    <h4 className="font-semibold mb-2 text-blue-600 dark:text-blue-400">❌ "Cannot schedule games"</h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm">
                      <strong>Solution:</strong> Make sure you have an active season. Only one season can be active at a time.
                      Go to Season Management → Find your season → Click "Activate"
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-blue-600 dark:text-blue-400">❌ "CSV import fails"</h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm">
                      <strong>Possible causes:</strong>
                      <ul className="mt-2 space-y-1">
                        <li>• Team names don't match existing teams exactly</li>
                        <li>• Invalid characters in player names</li>
                        <li>• Missing required columns</li>
                        <li>• File not saved as .csv</li>
                      </ul>
                      <strong>Solution:</strong> Check CSV format matches template exactly, verify team names.
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-blue-600 dark:text-blue-400">❌ "Playoff bracket not generating"</h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm">
                      <strong>Solution:</strong> Ensure regular season is complete with all games having results entered.
                      Check that standings are properly calculated before generating playoffs.
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-blue-600 dark:text-blue-400">❌ "Trade approvals not working"</h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm">
                      <strong>Solution:</strong> Check that both teams exist and players are still on their respective teams.
                      Ensure no conflicting trades are pending for the same players.
                    </div>
                  </div>

                  <div>
                    <h4 className="font-semibold mb-2 text-blue-600 dark:text-blue-400">❌ "User can't access admin features"</h4>
                    <div className="bg-blue-50 dark:bg-blue-900/20 p-3 rounded text-sm">
                      <strong>Solution:</strong> Verify user role in User Management. Promote user to 'admin' or 'owner' role as needed.
                      User may need to log out and log back in for role changes to take effect.
                    </div>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-green-600 dark:text-green-400">Best Practices</h3>
                
                <div className="grid md:grid-cols-2 gap-6 text-sm">
                  <div>
                    <h4 className="font-semibold mb-3">🔄 Regular Maintenance:</h4>
                    <ul className="space-y-2">
                      <li>• Backup database weekly</li>
                      <li>• Clear expired invite codes monthly</li>
                      <li>• Review trade patterns for fairness</li>
                      <li>• Monitor user activity logs</li>
                      <li>• Update player ratings seasonally</li>
                    </ul>
                  </div>
                  <div>
                    <h4 className="font-semibold mb-3">⚠️ Prevention Tips:</h4>
                    <ul className="space-y-2">
                      <li>• Always test CSV imports with small files first</li>
                      <li>• Keep team names consistent across seasons</li>
                      <li>• Document any custom rule changes</li>
                      <li>• Communicate major changes to team owners</li>
                      <li>• Maintain current backups before major operations</li>
                    </ul>
                  </div>
                </div>
              </section>

              <section className="bg-white dark:bg-gray-800 p-6 rounded-lg shadow">
                <h3 className="text-lg font-semibold mb-4 text-purple-600">Emergency Procedures</h3>
                
                <div className="space-y-4 text-sm">
                  <div>
                    <h4 className="font-semibold">🆘 If data appears corrupted:</h4>
                    <ol className="mt-2 space-y-1">
                      <li>1. Stop all user activity immediately</li>
                      <li>2. Check recent backup files</li>
                      <li>3. Identify scope of corruption</li>
                      <li>4. Restore from most recent clean backup</li>
                      <li>5. Communicate issue to affected users</li>
                    </ol>
                  </div>
                  
                  <div>
                    <h4 className="font-semibold">🔄 If major feature stops working:</h4>
                    <ol className="mt-2 space-y-1">
                      <li>1. Check browser console for errors</li>
                      <li>2. Try refreshing page/clearing cache</li>
                      <li>3. Test with different browser/device</li>
                      <li>4. Review recent system changes</li>
                      <li>5. Contact technical support if needed</li>
                    </ol>
                  </div>
                </div>
              </section>

              <section className="bg-yellow-50 border border-yellow-200 dark:border-yellow-700 rounded-lg p-4">
                <h3 className="font-semibold text-yellow-800 mb-2">📞 Getting Help</h3>
                <p className="text-yellow-700 dark:text-yellow-400 text-sm mb-2">
                  If you encounter issues not covered here:
                </p>
                <ul className="text-yellow-600 text-sm space-y-1">
                  <li>• Document exact error messages or unexpected behavior</li>
                  <li>• Note what actions led to the problem</li>
                  <li>• Check if issue affects all users or just specific ones</li>
                  <li>• Have backup information ready if data restoration is needed</li>
                </ul>
              </section>
            </div>
          </div>
        );

      default:
        return <div>Select a section from the sidebar</div>;
    }
  };

  return (
    <div className="min-h-screen bg-gray-100 dark:bg-gray-700 flex">
      {/* Sidebar */}
      <div className="w-80 bg-white dark:bg-gray-800 shadow-lg dark:shadow-gray-900 overflow-y-auto">
        <div className="p-6 border-b border-gray-200 dark:border-gray-700">
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">📚 {t('admin.adminDocs.title')}</h1>
          <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">{t('admin.adminDocs.subtitle')}</p>
        </div>
        
        <nav className="p-4">
          <div className="space-y-2">
            {sections.map((section) => (
              <button
                key={section.id}
                onClick={() => setActiveSection(section.id)}
                className={`w-full text-left px-4 py-3 rounded-lg transition-colors flex items-center ${
                  activeSection === section.id
                    ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-800 border border-blue-200 dark:border-blue-700'
                    : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:bg-gray-700'
                }`}
              >
                <span className="text-lg mr-3">{section.icon}</span>
                <span className="font-medium">{section.title}</span>
              </button>
            ))}
          </div>
        </nav>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="max-w-4xl mx-auto p-8">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default AdminDocumentation;